/**
 * A fixture as the Contest Overview's Fixtures card shows it — crests, a title,
 * and a meta line of kickoff · status · markets (design-system.md §6 "Fixture
 * row"; `.fixture` in the prototype's contest-overview.css).
 *
 * > **This is the Overview's row, not the catalogue's.** The handoff draws two.
 * > `.fixture` (here) is an inset tile on `--surface-muted` whose meta line is
 * > dot-separated plain text with a small dotless `.badge`. `.m-row` — the
 * > catalogue, Edit › Fixtures and the Add Fixtures modal — is a white card with
 * > a checkbox, a chevron, a full `StatusPill` and a bordered `MarketBadge`.
 * > Same information, deliberately different weight: one is a detail on a
 * > dashboard, the other a row you select and expand. The screen tickets that
 * > need the second one compose it from `StatusPill` and `MarketBadge`, which
 * > this kit exports for exactly that.
 *
 * Tournament fixtures have no opposing side, so they render one crest and drop
 * the "vs" rather than titling themselves "Wimbledon vs undefined".
 *
 * The row is a button only when `onOpen` is given. A `div` with a click handler
 * would take the pointer and leave the keyboard out, and a button with nothing
 * to do is a focus stop that goes nowhere — so the element follows the prop.
 */
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { Theme } from '@mui/material/styles';
import { Crests, type CrestTeam } from './crests';
import { ExternalLinkIcon } from './line-icons';

/** What a fixture can be, per the Contest Overview's own data shape. */
export type FixtureStatus = 'upcoming' | 'live' | 'finished';

const STATUS_LABELS: Record<FixtureStatus, string> = {
  upcoming: 'Upcoming',
  live: 'Live',
  finished: 'Finished',
};

interface FixtureRowProps {
  home: CrestTeam;
  /** Omitted for a tournament fixture. */
  away?: CrestTeam;
  /**
   * What to call the fixture, when the two teams are not the answer.
   *
   * Defaults to `{Home} vs {Away}`, which is what the design draws and what a
   * head-to-head wants. It is not universal: a tournament fixture ("FIFA World
   * Cup 2026 - Group A") has no sides and the feed sends both team fields
   * empty, and an operator can rename any fixture with a display-name
   * override. A caller holding the canonical label — `resolveFixtureLabel`
   * from `@s2w/core` — passes it here.
   */
  title?: string;
  /** Pre-formatted, e.g. "25 May 2026 · 12:00". */
  kickoff: string;
  status: FixtureStatus;
  /**
   * How many markets the feed has for this fixture, or `undefined` while that
   * is still being fetched.
   *
   * The two are different claims and the row draws them differently. A count of
   * zero is an answer — the feed has nothing for this fixture, which is the
   * thing an operator acts on — so it prints. An unresolved count prints
   * nothing at all rather than borrowing zero's appearance for a second, which
   * is the same "no stats slop" rule the KPI strip's idle state follows.
   */
  marketCount?: number;
  /**
   * The count was asked for and did not arrive — the catalogue errored.
   *
   * Its own flag because `marketCount: undefined` already means "not yet", and
   * the two must not look alike: an operator who sees nothing assumes the number
   * is still coming and waits for a row that will never fill in. A failed fetch
   * says so instead, which is the only honest third thing to draw. Ignored once
   * a count exists — a stale number beats an error about refreshing it.
   */
  marketCountUnavailable?: boolean;
  onOpen?: () => void;
}

/** `.fixture-meta .sep` — a literal middot in `--ink-4`, not a drawn dot. */
const Separator = () => (
  <Box aria-hidden component="span" sx={(theme: Theme) => ({ color: theme.tokens.color.ink4 })}>
    ·
  </Box>
);

export const FixtureRow = ({
  home,
  away,
  title,
  kickoff,
  status,
  marketCount,
  marketCountUnavailable = false,
  onOpen,
}: FixtureRowProps) => {
  /*
   * `||`, not `??`. The caller passes `resolveFixtureLabel(fixture)`, which
   * returns the feed's `name` verbatim and is only guarded as `typeof name ===
   * 'string'` — an empty string reaches here intact, and `??` would keep it,
   * blanking a row whose two teams would have titled it perfectly well.
   */
  const heading = title || (away ? `${home.name} vs ${away.name}` : home.name);

  return (
    <Box
      component={onOpen ? ButtonBase : 'div'}
      // Deliberately no `aria-label`: the row's own text already names it, and an
      // aria-label would *replace* that computed name rather than add to it —
      // leaving a screen-reader user with "Kolding vs Hillerod" while a sighted
      // one reads the kickoff, status and market count on the same line.
      {...(onOpen ? { onClick: onOpen } : {})}
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '14px',
        width: '100%',
        textAlign: 'left',
        borderRadius: `${theme.tokens.radius.insetRow}px`,
        backgroundColor: theme.tokens.color.surfaceMuted,
        border: theme.tokens.border.default,
        transition: 'background 0.12s',
        ...(onOpen
          ? { '&:hover': { backgroundColor: theme.tokens.componentColor.fixtureRowHover } }
          : {}),
      })}
    >
      <Crests home={home} away={away} />

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <Box
          component="span"
          sx={(theme: Theme) => ({
            fontSize: '14.5px',
            fontWeight: 600,
            letterSpacing: '-0.005em',
            color: theme.tokens.color.ink,
          })}
        >
          {heading}
        </Box>

        <Box
          sx={(theme: Theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            fontSize: '12.5px',
            color: theme.tokens.color.ink3,
            fontVariantNumeric: 'tabular-nums',
          })}
        >
          {/* Mono on the numbers only, per §1's "Numbers in monospace" — the
              badge's word stays in the body face. */}
          <Box component="span" sx={(theme: Theme) => ({ fontFamily: theme.tokens.font.mono })}>
            {kickoff}
          </Box>
          <Separator />
          <Box
            component="span"
            sx={(theme: Theme) => {
              const palette = theme.tokens.componentColor.fixtureBadge[status];
              return {
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '2px 8px',
                borderRadius: `${theme.tokens.radius.pill}px`,
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.01em',
                backgroundColor: palette.bg,
                color: palette.text,
              };
            }}
          >
            {STATUS_LABELS[status]}
          </Box>
          {marketCount !== undefined ? (
            <>
              <Separator />
              <Box component="span" sx={(theme: Theme) => ({ fontFamily: theme.tokens.font.mono })}>
                {marketCount} {marketCount === 1 ? 'market' : 'markets'}
              </Box>
            </>
          ) : marketCountUnavailable ? (
            <>
              <Separator />
              {/* Out of the mono face, which is for figures: there is no figure
                  here, and setting the sentence like one would have it read as
                  a reading rather than the absence of one. */}
              <Box
                component="span"
                sx={(theme: Theme) => ({ fontStyle: 'italic', color: theme.tokens.color.ink4 })}
              >
                Markets unavailable
              </Box>
            </>
          ) : null}
        </Box>
      </Box>

      {onOpen ? (
        <ExternalLinkIcon
          aria-hidden
          sx={(theme: Theme) => ({
            flexShrink: 0,
            fontSize: '11px',
            color: theme.tokens.color.accent,
          })}
        />
      ) : null}
    </Box>
  );
};
